// Auto-generated lightweight class for MBSE_Package
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { MBSE_PackageReadAPI, MBSE_PackageWriteAPI, MBSE_PackageReadAPISchema, MBSE_PackageWriteAPISchema } from '../types/mbse_package';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, ContainsPackage, DesignsPackage, HasMembers, MBSE_MainFunction, MBSE_Requirements, MBSE_RootPart } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { Workspace } from '../../base/api/workspace';
import { Thread } from '../../chat/api/thread';
import { MBSE_Requirement } from './mbse_requirement';
import { MBSE_Action } from './mbse_action';
import { MBSE_Part } from './mbse_part';



export interface MBSE_Package extends Partial<MBSE_PackageReadAPI> {}
export class MBSE_Package extends NeoNodeBase<MBSE_PackageReadAPI, MBSE_PackageWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "MBSE_Package";
  static readonly readSchema = MBSE_PackageReadAPISchema;
  static readonly writeSchema = MBSE_PackageWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    workspace: { target: "Workspace", cardinality: "one", isParent: false, relClass: "ContainsPackage", direction: "in" },
    dev_threads: { target: "Thread", cardinality: "many", isParent: false, relClass: "DesignsPackage", direction: "in" },
    requirements: { target: "MBSE_Requirement", cardinality: "many", isParent: false, relClass: "MBSE_Requirements", direction: "out" },
    main_function: { target: "MBSE_Action", cardinality: "one", isParent: false, relClass: "MBSE_MainFunction", direction: "out" },
    root_part: { target: "MBSE_Part", cardinality: "one", isParent: false, relClass: "MBSE_RootPart", direction: "out" },
  };

  protected get modelName(): string {
    return 'MBSE_Package';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return MBSE_Package.label;
  }


  constructor(data: Partial<MBSE_PackageWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: MBSE_PackageReadAPI, loadContext?: Record<string, any>): MBSE_Package {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, member_namespace, workspace, dev_threads, requirements, main_function, root_part, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = MBSE_PackageReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, member_namespace: member_namespace, workspace: workspace, dev_threads: dev_threads, requirements: requirements, main_function: main_function, root_part: root_part };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return MBSE_Package.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadOneRelationship(processed, 'workspace', 'Workspace', ModelRegistry);
    loadManyRelationship(processed, 'dev_threads', 'Thread', ModelRegistry);
    loadManyRelationship(processed, 'requirements', 'MBSE_Requirement', ModelRegistry);
    loadOneRelationship(processed, 'main_function', 'MBSE_Action', ModelRegistry);
    loadOneRelationship(processed, 'root_part', 'MBSE_Part', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new MBSE_Package(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'workspace' - Python ORM style (cached)
  get workspace(): RelationshipManager<this, Workspace> {
    return this.getOrCreateRelationshipManager('workspace', () => {
      return new RelationshipManager(this, 'workspace', 'one', ContainsPackage as typeof Relationship);
    });
  }

  // Relationship manager for 'dev_threads' - Python ORM style (cached)
  get dev_threads(): RelationshipManager<this, Thread> {
    return this.getOrCreateRelationshipManager('dev_threads', () => {
      return new RelationshipManager(this, 'dev_threads', 'many', DesignsPackage as typeof Relationship);
    });
  }

  // Relationship manager for 'requirements' - Python ORM style (cached)
  get requirements(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('requirements', () => {
      return new RelationshipManager(this, 'requirements', 'many', MBSE_Requirements as typeof Relationship);
    });
  }

  // Relationship manager for 'main_function' - Python ORM style (cached)
  get main_function(): RelationshipManager<this, MBSE_Action> {
    return this.getOrCreateRelationshipManager('main_function', () => {
      return new RelationshipManager(this, 'main_function', 'one', MBSE_MainFunction as typeof Relationship);
    });
  }

  // Relationship manager for 'root_part' - Python ORM style (cached)
  get root_part(): RelationshipManager<this, MBSE_Part> {
    return this.getOrCreateRelationshipManager('root_part', () => {
      return new RelationshipManager(this, 'root_part', 'one', MBSE_RootPart as typeof Relationship);
    });
  }

}
